import { create } from "zustand";
import { persist } from "zustand/middleware";
import { trackAddToCart } from "../lib/pixel";

const buildSkuFromId = (id) => `MDB-${String(id).padStart(4, "0")}`;
const getProductKey = (value) => {
  if (value && typeof value === "object") {
    if (value.sku) return String(value.sku);
    if (value.id !== undefined && value.id !== null)
      return buildSkuFromId(value.id);
  }
  if (typeof value === "number") return buildSkuFromId(value);
  return String(value);
};

const useStore = create(
  persist(
    (set, get) => ({
      // ============================================
      // CART STATE & ACTIONS
      // ============================================
      cart: [],

      // UPDATED: Now accepts selectedSize parameter
      addToCart: (product, selectedSize = null, customization = null) => {
        const { cart } = get();
        const productKey = getProductKey(product);
        trackAddToCart({
          ...product,
          sku: product.sku ?? buildSkuFromId(product.id),
        });

        // Find item with matching ID AND size
        const existing = cart.find(
          (item) =>
            getProductKey(item) === productKey &&
            item.selectedSize === selectedSize &&
            JSON.stringify(item.customization) === JSON.stringify(customization)
        );

        if (existing) {
          set({
            cart: cart.map((item) =>
              getProductKey(item) === productKey &&
              item.selectedSize === selectedSize &&
              JSON.stringify(item.customization) ===
                JSON.stringify(customization)
                ? { ...item, quantity: item.quantity + 1 }
                : item
            ),
          });
        } else {
          set({
            cart: [
              ...cart,
              {
                ...product,
                sku: product.sku ?? buildSkuFromId(product.id),
                quantity: 1,
                selectedSize,
                customization, // NEW: Store resizing info
              },
            ],
          });
        }
        const resizingText = customization?.needsResizing
          ? ` with ${customization.resizingType} (+$${customization.resizingFee})`
          : "";

        get().showToast(
          selectedSize
            ? `Added to cart (Size: ${selectedSize})${resizingText}!`
            : "Added to cart!"
        );
      },

      // UPDATED: Now accepts selectedSize parameter
      removeFromCart: (productRef, selectedSize = null) => {
        const targetKey = getProductKey(productRef);
        set({
          cart: get().cart.filter(
            (item) =>
              !(
                getProductKey(item) === targetKey &&
                item.selectedSize === selectedSize
              )
          ),
        });
      },

      // UPDATED: Now accepts selectedSize as second parameter
      updateQuantity: (productRef, selectedSize, change) => {
        const targetKey = getProductKey(productRef);
        const { cart } = get();
        set({
          cart: cart
            .map((item) => {
              if (
                getProductKey(item) === targetKey &&
                item.selectedSize === selectedSize
              ) {
                const newQuantity = item.quantity + change;
                return newQuantity > 0
                  ? { ...item, quantity: newQuantity }
                  : item;
              }
              return item;
            })
            .filter((item) => item.quantity > 0),
        });
      },

      clearCart: () => set({ cart: [] }),

      // NEW: Helper to check if specific size is in cart
      isInCart: (productId, selectedSize = null) => {
        const targetKey = getProductKey(productId);
        return get().cart.some(
          (item) =>
            getProductKey(item) === targetKey &&
            item.selectedSize === selectedSize
        );
      },

      // ============================================
      // WISHLIST STATE & ACTIONS (unchanged)
      // ============================================
      wishlist: [],

      addToWishlist: (product) => {
        const { wishlist } = get();
        const existing = wishlist.find((item) => item.id === product.id);

        if (!existing) {
          set({ wishlist: [...wishlist, product] });
          get().showToast("Added to wishlist!");
        }
      },

      removeFromWishlist: (productId) => {
        set({
          wishlist: get().wishlist.filter((item) => item.id !== productId),
        });
        get().showToast("Removed from wishlist");
      },

      toggleWishlist: (product) => {
        const { wishlist } = get();
        const existing = wishlist.find((item) => item.id === product.id);

        if (existing) {
          get().removeFromWishlist(product.id);
        } else {
          get().addToWishlist(product);
        }
      },

      isInWishlist: (productId) => {
        return get().wishlist.some((item) => item.id === productId);
      },

      // ============================================
      // UI MODALS STATE
      // ============================================
      cartOpen: false,
      searchOpen: false,
      mobileMenuOpen: false,

      setCartOpen: (isOpen) => set({ cartOpen: isOpen }),
      setSearchOpen: (isOpen) => set({ searchOpen: isOpen }),
      setMobileMenuOpen: (isOpen) => set({ mobileMenuOpen: isOpen }),

      closeAllModals: () =>
        set({
          cartOpen: false,
          searchOpen: false,
          mobileMenuOpen: false,
        }),

      // ============================================
      // TOAST NOTIFICATIONS
      // ============================================
      toast: null,

      showToast: (message, type = "success") => {
        set({ toast: { message, type } });
        setTimeout(() => set({ toast: null }), 3000);
      },

      hideToast: () => set({ toast: null }),
    }),
    {
      name: "jewelry-store",
      // v2: products moved from the hardcoded file (numeric ids) to PocketBase
      // (string ids). Carts saved under v1 reference products that no longer
      // resolve, so they're dropped rather than silently misbehaving.
      version: 2,
      migrate: () => ({ cart: [], wishlist: [] }),
      partialize: (state) => ({
        cart: state.cart,
        wishlist: state.wishlist,
      }),
    }
  )
);

export default useStore;
